import React, { useEffect, useState } from 'react';
import { Loader2, DollarSign, ShoppingBag, Clock, TrendingUp, AlertTriangle, Trophy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, accent, testId }) => (
  <div className="bg-white rounded-lg shadow-md p-5 flex items-center gap-4" data-testid={testId}>
    <div className={`p-3 rounded-full ${accent}`}>
      <Icon className="h-6 w-6 text-white" />
    </div>
    <div>
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-[#001B2A]">{value}</p>
    </div>
  </div>
);

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/admin/stats`, { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => setStats(data && data.daily_revenue ? data : null))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#7B1E2B]" /></div>;
  if (!stats) return <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">Could not load stats.</div>;

  const maxDaily = Math.max(...stats.daily_revenue.map((d) => d.revenue), 1);

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={DollarSign} label="Today's Revenue" value={`$${stats.today_revenue.toFixed(2)}`} accent="bg-green-600" testId="stat-today-revenue" />
        <StatCard icon={TrendingUp} label="Total Revenue" value={`$${stats.total_revenue.toFixed(2)}`} accent="bg-[#7B1E2B]" testId="stat-total-revenue" />
        <StatCard icon={ShoppingBag} label="Total Orders" value={stats.total_orders} accent="bg-[#001B2A]" testId="stat-total-orders" />
        <StatCard icon={Clock} label="Pending Orders" value={stats.pending_orders} accent="bg-yellow-500" testId="stat-pending-orders" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6" data-testid="daily-revenue-chart">
          <h3 className="font-serif font-bold text-[#001B2A] mb-4">Revenue — Last 7 Days</h3>
          <div className="flex items-end gap-2 h-40">
            {stats.daily_revenue.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[10px] text-gray-500">${d.revenue.toFixed(0)}</span>
                <div
                  className="w-full bg-[#7B1E2B] rounded-t transition-all"
                  style={{ height: `${Math.max((d.revenue / maxDaily) * 100, 2)}%` }}
                  title={`${d.date}: $${d.revenue.toFixed(2)}`}
                />
                <span className="text-[10px] text-gray-400">
                  {new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' })}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6" data-testid="best-sellers-list">
          <h3 className="font-serif font-bold text-[#001B2A] mb-4 flex items-center gap-2">
            <Trophy className="h-5 w-5 text-[#E6A817]" /> Best Sellers
          </h3>
          {stats.best_sellers.length === 0 ? (
            <p className="text-sm text-gray-500">No paid orders yet — best sellers will appear here.</p>
          ) : (
            <div className="space-y-3">
              {stats.best_sellers.map((s, idx) => (
                <div key={s.name} className="flex items-center gap-3 text-sm">
                  <span className="w-6 text-center font-bold text-gray-400">#{idx + 1}</span>
                  {s.image && <img src={s.image} alt={s.name} className="w-9 h-9 rounded object-cover" />}
                  <span className="flex-1 text-gray-700 font-medium">{s.name}</span>
                  <span className="text-gray-500">{s.quantity} sold</span>
                  <span className="font-semibold text-[#001B2A]">${s.revenue.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6" data-testid="low-stock-alerts">
        <h3 className="font-serif font-bold text-[#001B2A] mb-4 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-500" /> Low Stock Alerts
          <span className="text-xs font-normal text-gray-400">({stats.low_stock_threshold} or fewer left)</span>
        </h3>
        {stats.low_stock.length === 0 ? (
          <p className="text-sm text-gray-500">All good — no products are running low. ✅</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {stats.low_stock.map((p) => (
              <div key={p.id} className="flex items-center gap-3 border border-red-100 bg-red-50 rounded-md p-3 text-sm" data-testid={`low-stock-${p.id}`}>
                {p.image && <img src={p.image} alt={p.name} className="w-10 h-10 rounded object-cover" />}
                <span className="flex-1 font-medium text-gray-700">{p.name}</span>
                <span className={`font-bold px-2 py-0.5 rounded-full text-xs ${p.stock <= 0 ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700'}`}>
                  {p.stock <= 0 ? 'OUT OF STOCK' : `${p.stock} left`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
